import React, { useEffect, useState } from 'react';

import { Typography } from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import { withStyles } from '@mui/styles';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { change, Field, getFormValues, reduxForm } from 'redux-form/immutable';
import { createStructuredSelector } from 'reselect';

import Button from 'components/Button';
import CleanableTextField from 'components/CleanableTextField';
import Dialog from 'components/dialogs/Dialog';
import DialogSubmit from 'components/dialogs/DialogSubmit';
import LoadingIndicator from 'components/LoadingIndicator';
import RemoveScrollbar from 'components/RemoveScrollbar';
import useCurrentUser from 'hooks/useCurrentUser';
import useGlobalErrorHandler from 'hooks/useGlobalErrorHandler';
import {
  useSendValidationEmailMutation,
  useValidateEmailMutation,
} from 'redux/api/registration.api';
import { useLazyFetchUserInfoQuery } from 'redux/api/userInfos.api';
import { FONTSIZE_S } from 'theme/typography';
import globalMessages from 'translations/messages/global-messages';
import Analytics from 'utils/Analytics';
import localStorageUser from 'utils/localStorageUser';

export const styles = theme => ({
  container: {
    padding: '3.5rem 2.5rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    alignItems: 'center',
  },
  subtitle: {
    color: theme.palette.text.primary,
    fontSize: FONTSIZE_S,
  },
  form: {
    width: '100%',
  },
});

const VerifyCode = ({
  classes,
  open,
  formValues,
  dispatchChangeFormValue,
  validationCode = null,
  handleSubmit,
  onClose,
}) => {
  const intl = useIntl();
  const { userId: currentUserId, email: currentUserEmail } = useCurrentUser();
  const [loading, setLoading] = useState(false);
  const [disableResend, setDisableResend] = useState(false);
  const [validateEmailMutation] = useValidateEmailMutation();
  const [sendValidationEmailMutation] = useSendValidationEmailMutation();
  const [refetchUserInfo] = useLazyFetchUserInfoQuery();
  const dispatchGlobalError = useGlobalErrorHandler();

  useEffect(
    () => {
      if (validationCode) {
        dispatchChangeFormValue('enter-code', 'code', validationCode);
      }
      return () => {
        localStorageUser.setEmailValidationLocation(null);
        localStorageUser.setEmailVerificationLink(null);
      };
    },
    [validationCode, dispatchChangeFormValue],
  );

  const handleSendValidationEmail = () => {
    setDisableResend(true);
    Analytics.track('users_validate_email_resend', {
      source: 'user_settings',
    });
    setTimeout(() => {
      setDisableResend(false);
    }, 30000);
    localStorageUser.setEmailValidationLocation(window.location.pathname);
    sendValidationEmailMutation({ email: currentUserEmail });
  };

  const valuesForm = JSON.stringify(formValues, null, 2);
  const { code } = valuesForm ? JSON.parse(valuesForm) : '';

  const onSubmit = async () => {
    try {
      setLoading(true);
      await validateEmailMutation({ email: currentUserEmail, code: `F-${code}` }).unwrap();
      await new Promise(resolve => setTimeout(resolve, 2000));
      await refetchUserInfo({ userId: currentUserId }, false).unwrap();
      onClose();
    } catch (err) {
      dispatchGlobalError({
        error: err,
        codeToMessageIntl: {
          INCORRECT_VALIDATION_CODE: globalMessages.invalid_code_try_again,
          VALIDATION_CODE_EXPIRED: globalMessages.expired_code,
        },
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      id="dialog-enter-code"
      open={open}
      fullWidth
      maxWidth="sm"
      onCloseDialog={onClose}
      dialogTitleNode={intl.formatMessage(globalMessages.enter_your_code)}
    >
      <RemoveScrollbar>
        {loading && <LoadingIndicator size="40" />}
        <div className={classes.container}>
          <Typography className={classes.subtitle}>
            {intl.formatMessage(globalMessages.validation_email_sent)}
          </Typography>
          <form
            id="enter-code"
            className={classNames(classes.form, {
              [classes.loading]: loading,
            })}
            onSubmit={handleSubmit(onSubmit)}
          >
            <Field
              name="code"
              required
              type="text"
              autoComplete="one-time-code"
              inputMode="numeric"
              pattern="\d{6}"
              customClassName={classes.textField}
              component={CleanableTextField}
              placeholder={intl.formatMessage(globalMessages.type_code_here)}
              customValueOutput={value => value.replace(/\D/g, '')}
              InputProps={{
                startAdornment: <InputAdornment position="start">F-</InputAdornment>,
              }}
            />
          </form>
          <Button variant="plain" disabled={disableResend} onClick={handleSendValidationEmail}>
            {intl.formatMessage(globalMessages.resend_the_email)}
          </Button>
        </div>
        <DialogSubmit
          form="enter-code"
          onClose={onClose}
          submitLabel={intl.formatMessage(globalMessages.validate)}
        />
      </RemoveScrollbar>
    </Dialog>
  );
};

VerifyCode.propTypes = {
  classes: PropTypes.object.isRequired,
  handleSubmit: PropTypes.func,
  dispatchChangeFormValue: PropTypes.func,
  formValues: PropTypes.object,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  validationCode: PropTypes.string,
};

const mapStateToProps = createStructuredSelector({
  formValues: getFormValues('enter-code'),
});

const mapDispatchToProps = {
  dispatchChangeFormValue: change,
};

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withStyles(styles),
  withConnect,
  reduxForm({
    destroyOnUnmount: true,
    enableReinitialize: true,
    form: 'enter-code',
  }),
)(VerifyCode);
